import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import { inventoryApi } from '../api';
import { useClinicEvent } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { formatDate, formatMoney, timeAgo, toInputDate } from '../utils/format';

const TYPES = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drops', 'Ointment', 'Powder', 'Inhaler', 'Other'];
/** What one pack is called. Pick "Tablet" when they are bought loose. */
const UNITS = ['Strip', 'Bottle', 'Tube', 'Sachet', 'Box', 'Vial', 'Tablet', 'Capsule'];

/**
 * Only these hold several pieces inside them. A bottle or a tube is handed over
 * whole, so asking how many pieces are in one would make no sense - it is one.
 */
const PACKS_WITH_PIECES = ['Strip', 'Box'];
const holdsPieces = (unit) => PACKS_WITH_PIECES.includes(unit);

const EMPTY_NEW = {
  name: '',
  type: 'Tablet',
  unit: 'Strip',
  piecesPerPack: '10',
  price: '',
  packs: '',
  expiryDate: '',
};

const EMPTY_STOCK = {
  reason: 'purchase',
  packs: '',
  countedPacks: '',
  countedPieces: '',
  piecesPerPack: '',
  price: '',
  expiryDate: '',
  note: '',
};

/** "12 Strips (120 tablets)" - or just the pieces when a pack is one piece. */
function stockLabel(m) {
  const pieces = m.quantity || 0;
  const perPack = m.piecesPerPack || 1;
  if (perPack === 1) return `${pieces} ${m.unit || ''}`.trim();

  const packs = Math.floor(pieces / perPack);
  const loose = pieces - packs * perPack;
  const parts = [];
  if (packs) parts.push(`${packs} ${m.unit}${packs === 1 ? '' : 's'}`);
  if (loose) parts.push(`${loose} loose`);
  return parts.join(' + ') || '0';
}

const REASON_LABEL = {
  purchase: 'Stock received',
  opening: 'Opening stock',
  dispensed: 'Given to patient',
  correction: 'Count corrected',
  expired: 'Removed - expired',
};

export default function MedicineInventory() {
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ value: 0, lowStock: 0, outOfStock: 0, expired: 0 });
  const [lowStockAtPacks, setLowStockAtPacks] = useState(2);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [adding, setAdding] = useState(null); // the "new medicine" dialog
  const [stockFor, setStockFor] = useState(null); // the "add stock" dialog
  const [stockForm, setStockForm] = useState(EMPTY_STOCK);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await inventoryApi.list();
      setRows(res.data);
      setTotals(res.totals);
      setLowStockAtPacks(res.lowStockAtPacks);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // A prescription finished on the doctor's screen changes the counts here.
  useClinicEvent(['inventory:changed'], load);

  async function saveNew(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await inventoryApi.create(adding);
      toast.success(`${adding.name.trim()} added to the inventory`);
      setAdding(null);
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function saveStock(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await inventoryApi.addStock(stockFor._id, stockForm);
      toast.success(`${stockFor.name}: ${stockLabel(res.data)} on the shelf now`);
      setStockFor(null);
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function openHistory(row) {
    setHistoryFor(row);
    setHistory([]);
    try {
      const res = await inventoryApi.movements(row._id);
      setHistory(res.data);
    } catch (err) {
      toast.error(err);
    }
  }

  const term = search.trim().toLowerCase();
  const visible = term ? rows.filter((r) => r.name.toLowerCase().includes(term)) : rows;

  return (
    <>
      <PageHeader
        title="Medicine Inventory"
        subtitle="What is on the shelf, and what has gone out"
        icon="bi-box-seam"
      >
        <Button variant="outline-secondary" onClick={load} title="Refresh" className="me-2">
          <i className="bi bi-arrow-clockwise" />
        </Button>
        <Button className="btn-clinic" onClick={() => setAdding({ ...EMPTY_NEW })}>
          <i className="bi bi-plus-lg me-1" /> New Medicine
        </Button>
      </PageHeader>

      <Row className="g-4 mb-4">
        <Col xs={6} lg={3}>
          <StatCard label="Stock Value" value={formatMoney(totals.value || 0)} icon="bi-cash-stack" color="#2f4bb5" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard label="Running Low" value={totals.lowStock} icon="bi-exclamation-triangle-fill" color="#d9a213" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard label="Out of Stock" value={totals.outOfStock} icon="bi-x-octagon-fill" color="#e0563f" />
        </Col>
        <Col xs={6} lg={3}>
          <StatCard label="Expired" value={totals.expired} icon="bi-calendar-x-fill" color="#1f9d61" />
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>
            <i className="bi bi-capsule me-2" />
            {rows.length} medicine{rows.length === 1 ? '' : 's'} tracked
          </span>
          <InputGroup style={{ maxWidth: 320 }}>
            <InputGroup.Text>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search medicines..."
            />
          </InputGroup>
        </Card.Header>

        {loading ? (
          <Loader label="Loading the inventory..." className="py-5" />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="bi-box-seam"
            title={rows.length === 0 ? 'Nothing in the inventory yet' : 'Nothing matches that search'}
            hint={rows.length === 0 ? 'Add the first medicine with the button above.' : undefined}
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Medicine</th>
                  <th>Pack</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">In Stock</th>
                  <th className="text-end">Value</th>
                  <th>Expires</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => {
                  const qty = m.quantity || 0;
                  return (
                    <tr key={m._id}>
                      <td>
                        <div className="fw-semibold">{m.name}</div>
                        <div className="text-muted small">{m.type}</div>
                      </td>
                      <td className="text-muted small">
                        {m.piecesPerPack > 1 ? `${m.unit} of ${m.piecesPerPack}` : m.unit}
                      </td>
                      <td className="text-end small">
                        {m.price ? (
                          <>
                            <div>{formatMoney(m.price)}</div>
                            {m.piecesPerPack > 1 && (
                              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                {formatMoney(m.pricePerPiece)} each
                              </div>
                            )}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="text-end">
                        <span className={qty <= 0 ? 'text-danger fw-bold' : 'fw-semibold'}>{stockLabel(m)}</span>
                        {qty <= 0 ? (
                          <Badge bg="danger" className="ms-2">
                            Out
                          </Badge>
                        ) : m.packs < lowStockAtPacks ? (
                          <Badge bg="warning" text="dark" className="ms-2">
                            Low
                          </Badge>
                        ) : null}
                      </td>
                      <td className="text-end small">{m.price ? formatMoney(m.value) : '-'}</td>
                      <td className="small">
                        {m.expiryDate ? (
                          <span className={m.expired ? 'text-danger fw-semibold' : ''}>
                            {formatDate(m.expiryDate)}
                            {m.expired ? ' (expired)' : ''}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-1"
                          title={`Add stock of ${m.name}`}
                          onClick={() => {
                            setStockFor(m);
                            setStockForm({
                              ...EMPTY_STOCK,
                              piecesPerPack: m.piecesPerPack || 1,
                              price: m.price || '',
                              expiryDate: toInputDate(m.expiryDate),
                            });
                          }}
                        >
                          <i className="bi bi-plus-lg me-1" /> Stock
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="btn-icon"
                          title={`Movement history of ${m.name}`}
                          onClick={() => openHistory(m)}
                        >
                          <i className="bi bi-clock-history" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {/* ------------------------- new medicine ------------------------- */}
      <Modal show={!!adding} onHide={() => setAdding(null)} centered>
        {adding && (
          <Form onSubmit={saveNew}>
            <Modal.Header closeButton>
              <Modal.Title className="h5">
                <i className="bi bi-box-seam me-2" style={{ color: 'var(--clinic-primary)' }} />
                New Medicine
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={8}>
                  <Form.Label className="required">Name</Form.Label>
                  <Form.Control
                    required
                    autoFocus
                    value={adding.name}
                    onChange={(e) => setAdding({ ...adding, name: e.target.value })}
                    placeholder="e.g. Azithral 250"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Form</Form.Label>
                  <Form.Select value={adding.type} onChange={(e) => setAdding({ ...adding, type: e.target.value })}>
                    {TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={6}>
                  <Form.Label>Sold as</Form.Label>
                  <Form.Select
                    value={adding.unit}
                    onChange={(e) => {
                      const unit = e.target.value;
                      // A tube or a bottle is one piece by its nature.
                      setAdding({ ...adding, unit, piecesPerPack: holdsPieces(unit) ? adding.piecesPerPack : '1' });
                    }}
                  >
                    {UNITS.map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </Form.Select>
                </Col>
                {holdsPieces(adding.unit) && (
                  <Col md={6}>
                    <Form.Label>Pieces in one {adding.unit.toLowerCase()}</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={adding.piecesPerPack}
                      onChange={(e) => setAdding({ ...adding, piecesPerPack: e.target.value })}
                    />
                    <Form.Text className="text-muted">10 for a strip of ten.</Form.Text>
                  </Col>
                )}

                <Col md={6}>
                  <Form.Label>Price of one {adding.unit.toLowerCase()}</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>&#8377;</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={adding.price}
                      onChange={(e) => setAdding({ ...adding, price: e.target.value })}
                    />
                  </InputGroup>
                  {adding.price > 0 && adding.piecesPerPack > 1 && (
                    <Form.Text className="text-muted">
                      Works out at {formatMoney(adding.price / adding.piecesPerPack)} each - that is what the
                      patient is charged.
                    </Form.Text>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Label>How many {adding.unit.toLowerCase()}s do you have</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={adding.packs}
                    onChange={(e) => setAdding({ ...adding, packs: e.target.value })}
                    placeholder="0"
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Expiry Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={adding.expiryDate}
                    onChange={(e) => setAdding({ ...adding, expiryDate: e.target.value })}
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={() => setAdding(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" className="btn-clinic" disabled={busy}>
                <i className="bi bi-check-lg me-1" /> Add to Inventory
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* --------------------------- add stock --------------------------- */}
      <Modal show={!!stockFor} onHide={() => setStockFor(null)} centered>
        {stockFor && (
          <Form onSubmit={saveStock}>
            <Modal.Header closeButton>
              <Modal.Title className="h5">{stockFor.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="bg-light rounded p-3 mb-3 d-flex justify-content-between">
                <span className="text-muted">On the shelf now</span>
                <strong>{stockLabel(stockFor)}</strong>
              </div>

              <Row className="g-3">
                <Col xs={12}>
                  <Form.Label>What happened</Form.Label>
                  <Form.Select
                    value={stockForm.reason}
                    onChange={(e) => setStockForm({ ...stockForm, reason: e.target.value })}
                  >
                    <option value="purchase">New stock received</option>
                    <option value="correction">Correct the count after checking the shelf</option>
                  </Form.Select>
                </Col>

                {stockForm.reason === 'purchase' ? (
                  <>
                    <Col md={6}>
                      <Form.Label className="required">
                        {stockFor.unit}s received
                      </Form.Label>
                      <Form.Control
                        required
                        type="number"
                        min="1"
                        autoFocus
                        value={stockForm.packs}
                        onChange={(e) => setStockForm({ ...stockForm, packs: e.target.value })}
                      />
                    </Col>
                    {holdsPieces(stockFor.unit) && (
                      <Col md={6}>
                        <Form.Label>Pieces in one {stockFor.unit?.toLowerCase()}</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={stockForm.piecesPerPack}
                          onChange={(e) => setStockForm({ ...stockForm, piecesPerPack: e.target.value })}
                        />
                      </Col>
                    )}
                    <Col md={6}>
                      <Form.Label>Price of one {stockFor.unit?.toLowerCase()}</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>&#8377;</InputGroup.Text>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={stockForm.price}
                          onChange={(e) => setStockForm({ ...stockForm, price: e.target.value })}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <Form.Label>Expiry of this batch</Form.Label>
                      <Form.Control
                        type="date"
                        value={stockForm.expiryDate}
                        onChange={(e) => setStockForm({ ...stockForm, expiryDate: e.target.value })}
                      />
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={6}>
                      <Form.Label className="required">Full {stockFor.unit?.toLowerCase()}s counted</Form.Label>
                      <Form.Control
                        required
                        type="number"
                        min="0"
                        autoFocus
                        value={stockForm.countedPacks}
                        onChange={(e) => setStockForm({ ...stockForm, countedPacks: e.target.value })}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Loose pieces</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        value={stockForm.countedPieces}
                        onChange={(e) => setStockForm({ ...stockForm, countedPieces: e.target.value })}
                      />
                      <Form.Text className="text-muted">From a strip already opened.</Form.Text>
                    </Col>
                  </>
                )}

                <Col xs={12}>
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    value={stockForm.note}
                    onChange={(e) => setStockForm({ ...stockForm, note: e.target.value })}
                    placeholder="e.g. bill number, supplier"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={() => setStockFor(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" className="btn-clinic" disabled={busy}>
                <i className="bi bi-check-lg me-1" /> Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ---------------------------- history ---------------------------- */}
      <Modal show={!!historyFor} onHide={() => setHistoryFor(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            <i className="bi bi-clock-history me-2" style={{ color: 'var(--clinic-primary)' }} />
            {historyFor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {history.length === 0 ? (
            <EmptyState icon="bi-clock-history" title="Nothing has moved yet" />
          ) : (
            <Table className="mb-0 align-middle" size="sm">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th className="text-end">Change</th>
                  <th className="text-end">Left</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h._id}>
                    <td className="small">
                      {formatDate(h.createdAt)}
                      <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                        {timeAgo(h.createdAt)}
                      </div>
                    </td>
                    <td className="small">
                      {REASON_LABEL[h.reason] || h.reason}
                      {h.patientName ? <div className="text-muted">{h.patientName}</div> : null}
                      {h.note ? <div className="text-muted fst-italic">{h.note}</div> : null}
                    </td>
                    <td className={`text-end fw-semibold ${h.change < 0 ? 'text-danger' : 'text-success'}`}>
                      {h.change > 0 ? `+${h.change}` : h.change}
                    </td>
                    <td className="text-end">{h.balanceAfter}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setHistoryFor(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
